/**
 * iter650+iter651 — Timezone-aware date helpers.
 *
 * Fixes the "I pick the 1st but data starts on the 2nd" bug that was
 * affecting every report + filter page in the app. Root cause: native
 * `<input type="date">` returns a bare "YYYY-MM-DD" string with no
 * timezone, and `new Date(str).toISOString()` interprets it as UTC
 * midnight. For a Riyadh user (UTC+3) that's actually 03:00 local on
 * the same day, so the first 3 hours of the local day were excluded.
 *
 * These helpers are timezone-AGNOSTIC: they use the BROWSER'S local
 * timezone (whatever that may be: Asia/Riyadh, America/New_York,
 * Europe/London, etc.) so the same code works for every user in every
 * country without hardcoded offsets. Replaces every
 * `new Date().toISOString().split('T')[0]` callsite, which was always
 * UTC and therefore wrong after the user's local midnight.
 */

/** Browser's IANA timezone, e.g. "Asia/Riyadh". Falls back to UTC. */
export function getBrowserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

/** Format any Date instance as a local YYYY-MM-DD string (NOT UTC).
 *  Returns null for invalid input. */
export function toLocalYmd(d) {
  if (!d) return null;
  if (typeof d === 'string') return d.slice(0, 10);
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** R3 — Today's [y, m, d] in IANA `tz` (branch/company timezone) or, when
 *  `tz` is omitted/invalid, in the browser's local timezone. */
function todayParts(tz) {
  if (tz) {
    try {
      const s = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
      const p = s.split('-').map(Number);
      if (p.length === 3 && p.every(Number.isFinite)) return p;
    } catch { /* invalid tz → browser local */ }
  }
  const d = new Date();
  return [d.getFullYear(), d.getMonth() + 1, d.getDate()];
}
const _ymd = (y, m0, d) => new Date(Date.UTC(y, m0, d)).toISOString().slice(0, 10);

/** Today's date (YYYY-MM-DD) in `tz` or the user's local timezone.
 *  Use this when seeding <input type="date" /> defaults (do NOT use
 *  `new Date().toISOString().slice(0,10)` — that's UTC). */
export function localToday(tz) {
  const [y, m, d] = todayParts(tz);
  return _ymd(y, m - 1, d);
}

/** N days ago as YYYY-MM-DD. Negative for future. localDaysAgo(0) === localToday(). */
export function localDaysAgo(n, tz) {
  const [y, m, d] = todayParts(tz);
  return _ymd(y, m - 1, d - n);
}

/** First day of the current month as YYYY-MM-DD. */
export function localMonthStart(tz) {
  const [y, m] = todayParts(tz);
  return _ymd(y, m - 1, 1);
}

/** First day of the current CALENDAR year (Jan 1) as YYYY-MM-DD. */
export function localYearStart(tz) {
  const [y] = todayParts(tz);
  return _ymd(y, 0, 1);
}

/** Start of week (Sunday) as YYYY-MM-DD. */
export function localWeekStart(tz) {
  const [y, m, d] = todayParts(tz);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return _ymd(y, m - 1, d - dow);
}

/** Shared preset → {start, end} used by every date filter. */
export function presetRange(key, tz) {
  const today = localToday(tz);
  switch (key) {
    case 'today':     return { start: today, end: today };
    case 'yesterday': { const y = localDaysAgo(1, tz); return { start: y, end: y }; }
    case 'week':      return { start: localWeekStart(tz), end: today };
    case 'last7':     return { start: localDaysAgo(6, tz), end: today };
    case 'month':     return { start: localMonthStart(tz), end: today };
    case 'last30':    return { start: localDaysAgo(29, tz), end: today };
    case 'last90':    return { start: localDaysAgo(89, tz), end: today };
    case 'year':      return { start: localYearStart(tz), end: today };
    case 'last_week': { const ws = localWeekStart(tz); const [y, m, d] = ws.split('-').map(Number); return { start: _ymd(y, m - 1, d - 7), end: _ymd(y, m - 1, d - 1) }; }
    case 'last_month': { const [y, m] = todayParts(tz); return { start: _ymd(y, m - 2, 1), end: _ymd(y, m - 1, 0) }; }
    case 'last_year': { const [y] = todayParts(tz); return { start: _ymd(y - 1, 0, 1), end: _ymd(y - 1, 11, 31) }; }
    default:          return null;
  }
}

/** Report timezone rule (multi-country): when every branch in scope (the
 *  selected branches, else all branches) shares ONE configured timezone, presets
 *  use it; otherwise callers fall back to the COMPANY timezone (tenant.timezone),
 *  and only then the device timezone. One calendar range is applied to all
 *  selected branches — never shifted per branch. */
export function reportTimeZone(branches = [], selectedIds = []) {
  const sel = Array.isArray(selectedIds) ? selectedIds.filter(Boolean) : (selectedIds ? [selectedIds] : []);
  const pool = sel.length ? branches.filter((b) => sel.includes(b.id)) : branches;
  const tzs = [...new Set(pool.map((b) => b.timezone || ''))];
  return tzs.length === 1 && tzs[0] ? tzs[0] : undefined;
}

/** Convert a local YYYY-MM-DD picked from the UI into the UTC ISO
 *  string representing local midnight (start of that day in user tz). */
export function localStartOfDayUtc(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return undefined;
  const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
  const local = new Date(y, m - 1, d, 0, 0, 0, 0);
  return local.toISOString();
}

/** Local end-of-day (23:59:59.999) for a YYYY-MM-DD picker value. */
export function localEndOfDayUtc(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return undefined;
  const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
  const local = new Date(y, m - 1, d, 23, 59, 59, 999);
  return local.toISOString();
}

/** Convenience: from a {date_from, date_to} pair of YYYY-MM-DD picker
 *  values, produce the UTC range a backend can filter on. */
export function localRangeToUtc(date_from, date_to) {
  return {
    date_from: localStartOfDayUtc(date_from),
    date_to:   localEndOfDayUtc(date_to),
    tz: getBrowserTz(),
  };
}

/** Build a backend-storable "local datetime, no tz" stamp from a
 *  user-picked YYYY-MM-DD plus the user's current wall-clock time.
 *  Format: "YYYY-MM-DDTHH:mm:ss" (no Z, no offset). This preserves
 *  the user's intended local day on the server without UTC drift. */
export function localDateTimeStamp(yyyy_mm_dd) {
  const ymd = yyyy_mm_dd || localToday();
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `${ymd}T${hh}:${mm}:${ss}`;
}
